import { TransactionBlock } from "@mysten/sui.js";
import { bcsForVersion } from "@mysten/sui.js";
import { LAMPORT, OBJECT_RECORD } from "../config";
import { convertFloat, getProvider } from "../utils";
import { useEffect, useMemo, useState } from "react";
import { useWalletKit } from "@mysten/wallet-kit";
import BigNumber from "bignumber.js";
import { useStakings } from "../context";

export const useGetBalance = (account: string, updateNumber: number) => {
    const { changeVariable } = useStakings();
    const [data, setAccountBalance] = useState<string>("0");

    useEffect(() => {
        const getbalance = async () => {
            if (account === OBJECT_RECORD.ADDRESSZERO) setAccountBalance("0");
            else {
                const provider = getProvider();
                let accountBalance = await provider.getBalance({ owner: account });
                let balanceValue = convertFloat(
                    BigNumber(accountBalance?.totalBalance).dividedBy(LAMPORT)
                );
                changeVariable("balance", balanceValue);
                setAccountBalance(balanceValue);
            }
        };
        getbalance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account, updateNumber]);
    return data;
};

export const getAccount = async (account) => {
    const txb = new TransactionBlock();
    txb.moveCall({
        target: `${OBJECT_RECORD.PACKAGE_ID}::paw_presale::get_user`,
        arguments: [
            txb.object(OBJECT_RECORD.USER_STATE),
        ],
        typeArguments: [],
    });
    let provider = getProvider();
    const result = await provider.devInspectTransactionBlock({
        transactionBlock: txb,
        sender: account || OBJECT_RECORD.ADDRESSZERO,
    });

    if (result!["results"]) {
        const returnValues = result!["results"]![0]!["returnValues"];
        let purchasedAmount = bcsForVersion(await provider.getRpcApiVersion()).de(
            returnValues![0]![1],
            Uint8Array.from(returnValues![0]![0])
        );

        let isClaimed = bcsForVersion(await provider.getRpcApiVersion()).de(
            returnValues![1]![1],
            Uint8Array.from(returnValues![1]![0])
        );

        const results = {
            purchasedAmount: convertFloat(purchasedAmount / LAMPORT),
            isClaimed: isClaimed,
        };
        return results;
    } else {
        const results = {
            purchasedAmount: 0,
            isClaimed: false,
        };
        return results;
    }
};

export const useGetAccountInfo = (account: string, updateNumber: number) => {
    const [data, setdata] = useState<{}>(NaN);

    useMemo(() => {
        const getAccountInfo = async () => {
            const results = await getAccount(account);
            setdata(results);
        };
        getAccountInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account, updateNumber]);

    return data;
};

export const useStakingMethods = () => {
    const { signAndExecuteTransactionBlock } = useWalletKit();

    const staking = async (amount) => {
        const txb = new TransactionBlock();
        const [coin] = txb.splitCoins(txb.gas, [txb.pure(amount)]);
        const packageObjectId = OBJECT_RECORD.PACKAGE_ID;
        txb.moveCall({
            target: `${packageObjectId}::paw_presale::purchase`,
            arguments: [
                coin,
                txb.object(OBJECT_RECORD.USER_STATE),
                txb.object(OBJECT_RECORD.TREASURY),
                txb.object(OBJECT_RECORD.PRESALE),
                txb.object(OBJECT_RECORD.CLOCKOBJECT),
            ],
            typeArguments: [],
        });
        txb.setGasBudget(100000000);
        const tx = await signAndExecuteTransactionBlock({
            transactionBlock: txb,
            requestType: "WaitForEffectsCert",
            options: { showEffects: true },
        });
        return tx;
    };

    const claim = async () => {
        const txb = new TransactionBlock();
        const packageObjectId = OBJECT_RECORD.PACKAGE_ID;
        txb.moveCall({
            target: `${packageObjectId}::paw_presale::claim`,
            arguments: [
                txb.object(OBJECT_RECORD.USER_STATE),
                txb.object(OBJECT_RECORD.TREASURY),
                txb.object(OBJECT_RECORD.PRESALE),
                txb.object(OBJECT_RECORD.CLOCKOBJECT),
            ],
            typeArguments: [],
        });
        txb.setGasBudget(100000000);
        const tx = await signAndExecuteTransactionBlock({
            transactionBlock: txb,
            requestType: "WaitForEffectsCert",
            options: { showEffects: true },
        });
        return tx;
    };

    return { staking, claim };
};

export const useGetPresaleInfo = (updateNumber) => {
    const [poolInfo, setdata] = useState<{}>();

    const [status, setStatus] = useState("idle");
    useEffect(() => {
        const getPoolInfo = async () => {
            setStatus("fetching");
            const txb = new TransactionBlock();
            txb.moveCall({
                target: `${OBJECT_RECORD.PACKAGE_ID}::paw_presale::get_presale_state`,
                typeArguments: [],
                arguments: [txb.object(OBJECT_RECORD.PRESALE)],
            });

            let provider = getProvider();
            const result = await provider.devInspectTransactionBlock({
                transactionBlock: txb,
                sender: OBJECT_RECORD.ADDRESSZERO,
            });
            const returnValues = result!["results"]![0]!["returnValues"];
            let totalSupply = bcsForVersion(await provider.getRpcApiVersion()).de(
                returnValues![0]![1],
                Uint8Array.from(returnValues![0]![0])
            );

            let allocated = bcsForVersion(await provider.getRpcApiVersion()).de(
                returnValues![1]![1],
                Uint8Array.from(returnValues![1]![0])
            );

            let startTime = bcsForVersion(await provider.getRpcApiVersion()).de(
                returnValues![2]![1],
                Uint8Array.from(returnValues![2]![0])
            );

            let endTime = bcsForVersion(await provider.getRpcApiVersion()).de(
                returnValues![3]![1],
                Uint8Array.from(returnValues![3]![0])
            );

            let tokenPerSui = bcsForVersion(await provider.getRpcApiVersion()).de(
                returnValues![4]![1],
                Uint8Array.from(returnValues![4]![0])
            );

            let isClaimable = bcsForVersion(await provider.getRpcApiVersion()).de(
                returnValues![5]![1],
                Uint8Array.from(returnValues![5]![0])
            );

            const results = {
                totalSupply: BigNumber(totalSupply).dividedBy(LAMPORT),
                allocated: BigNumber(allocated).dividedBy(LAMPORT),
                startTime,
                endTime,
                tokenPerSui,
                isClaimable,
            };
            setdata(results);
            setStatus("fetched");
        };
        getPoolInfo();
    }, [updateNumber]);
    return { status, poolInfo };
};
